import { useEffect, useRef, useState } from 'react';

import {
  subscribeToGpsTrack,
  type LocalGpsPoint,
} from '../offline/gpsTrackStore';
import type { LiveTrackPoint } from './useSurveillanceRouteGpsTracking';

/**
 * Traza GPS del recorrido servida a quien NO pinta el mapa.
 *
 * ── El fallo que existe para impedir ───────────────────────────────────────
 *
 * El detector de cambio de sentido consumía `liveTrack`, que solo alimenta el
 * `watch` de la pantalla del mapa. Eso ataba una condición de negocio —«hay un
 * recorrido en curso que este dispositivo está haciendo»— al ciclo de vida de
 * un componente, que es el mismo defecto que costó 47 horas de captura muerta
 * el 12-ago-2026 y que `useGpsCaptureGuardian` ya vino a corregir.
 *
 * Dos consecuencias medidas, no teóricas:
 *
 *   1. Fuera de la pantalla del recorrido —creando una básica, mirando otra
 *      OT, o en la pestaña de al lado— el giro NO se detectaba en absoluto.
 *   2. Con la pantalla apagada, Android congela el WebView y el `watch` no
 *      dispara. El servicio nativo SÍ graba, pero sus puntos entran por
 *      `drainNativeGpsBuffer` → `appendGpsPoints`, que no tocaba `liveTrack`.
 *      El detector se quedaba con su último punto visto y estampaba ahí el
 *      corte: el 2026-09-01, en la C-25, dos veces, en la gasolinera.
 *
 * Aquí los puntos se toman de donde escriben LOS DOS caminos —el almacén
 * local— por suscripción y no por sondeo: releer IndexedDB cada pocos
 * segundos no es gratis con 20.000 puntos, y el sondeo además puede perderse
 * lo que entre y salga entre dos lecturas.
 *
 * 🔴 No siembra con lo ya guardado, a propósito: el detector debe arrancar en
 * el presente. Replayar la traza al montar registraría como recién ocurrido un
 * giro de hace horas — el mismo motivo por el que el propio detector adelanta
 * su cursor al último punto conocido en vez de rebobinarlo.
 */

/** Tope de puntos retenidos. El detector solo mira lo que hay tras su cursor. */
const MAX_FEED_POINTS = 500;

const toTrackPoint = (point: LocalGpsPoint): LiveTrackPoint => ({
  lat: point.latitude,
  lng: point.longitude,
  timestamp: point.timestamp,
  accuracy: point.accuracy,
  speed: point.speed,
  heading: point.heading,
});

export function useLocalGpsTrackFeed(
  routeId: string | undefined,
  enabled: boolean
): LiveTrackPoint[] {
  const [track, setTrack] = useState<LiveTrackPoint[]>([]);

  // Un recorrido distinto es una traza distinta: mezclarlas haría medir
  // retrocesos entre dos patrullas que no tienen nada que ver.
  const routeRef = useRef(routeId);
  if (routeRef.current !== routeId) {
    routeRef.current = routeId;
  }

  useEffect(() => {
    setTrack([]);
    if (!routeId || !enabled) return undefined;

    return subscribeToGpsTrack(routeId, (points) => {
      setTrack((previous) =>
        [...previous, ...points.map(toTrackPoint)].slice(-MAX_FEED_POINTS)
      );
    });
  }, [routeId, enabled]);

  return track;
}

export default useLocalGpsTrackFeed;
